import { validateChartSpec } from './chat-blocks.mjs';
import { calendarDay } from './data-dashboards.mjs';

const scalar = value => value === null || ['string', 'boolean'].includes(typeof value)
  || (typeof value === 'number' && Number.isFinite(value));

function jsonCell(value, ancestors = []) {
  if (scalar(value)) return true;
  if (!value || typeof value !== 'object' || ancestors.includes(value) || ancestors.length >= 1000) return false;
  if (!Array.isArray(value) && Object.getPrototypeOf(value) !== Object.prototype) return false;
  return Object.values(value).every(child => jsonCell(child, [...ancestors, value]));
}

export function validResult(artifact) {
  if (!artifact || artifact.kind !== 'query_result' || !['table', 'chart', 'tree'].includes(artifact.view)) return false;
  if (artifact.chart_type !== undefined && !['bar', 'line', 'doughnut'].includes(artifact.chart_type)) return false;
  if (![artifact.title, artifact.company].every(value => typeof value === 'string' && value.trim()) || typeof artifact.query !== 'string') return false;
  if (typeof artifact.captured_at !== 'string' || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/.test(artifact.captured_at) || !Number.isFinite(Date.parse(artifact.captured_at))) return false;
  const { columns, rows } = artifact;
  if (!Array.isArray(columns) || !columns.length || !columns.every(column => typeof column === 'string' && column.trim()) || new Set(columns).size !== columns.length) return false;
  if (!Array.isArray(rows) || !rows.every(row => Array.isArray(row) && row.length === columns.length && row.every(cell => jsonCell(cell)))) return false;
  if (typeof artifact.truncated !== 'boolean' || !Number.isSafeInteger(artifact.returned_rows) || artifact.returned_rows < rows.length) return false;
  if (artifact.source_id !== undefined && (typeof artifact.source_id !== 'string' || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(artifact.source_id))) return false;
  if (artifact.save_unavailable !== undefined && typeof artifact.save_unavailable !== 'boolean') return false;
  if (artifact.date_range !== undefined && artifact.date_range !== null) {
    try {
      calendarDay(artifact.date_range.start); calendarDay(artifact.date_range.end_exclusive);
      if (artifact.date_range.start >= artifact.date_range.end_exclusive) return false;
    } catch { return false; }
  }
  const references = artifact.view === 'chart' ? ['x', 'y'] : artifact.view === 'tree' ? ['parent', 'child'] : [];
  return references.every(key => columns.includes(artifact[key])) && (!references.length || artifact[references[0]] !== artifact[references[1]]);
}

export function cellText(value) {
  return value === null ? '—' : typeof value === 'object' ? JSON.stringify(value) : String(value);
}

// Compare decimal text directly so PostgreSQL decimal/bigint strings retain their order.
function decimal(value) {
  if (!['string', 'number'].includes(typeof value)) return null;
  const match = String(value).trim().match(/^([+-]?)(\d*)(?:\.(\d*))?(?:e([+-]?\d+))?$/i);
  if (!match || !(match[2] || match[3]) || Math.abs(Number(match[4] || 0)) > 400) return null;
  const digits = (match[2] || '0') + (match[3] || '');
  const split = (match[2] || '0').length + Number(match[4] || 0);
  const integer = (split <= 0 ? '0' : split >= digits.length ? digits.padEnd(split, '0') : digits.slice(0, split)).replace(/^0+(?=\d)/, '');
  const fraction = (split <= 0 ? '0'.repeat(-split) + digits : digits.slice(split)).replace(/0+$/, '');
  return { sign: match[1] === '-' && (integer !== '0' || fraction) ? -1 : 1, integer, fraction };
}

function compareCells(a, b) {
  const left = decimal(a); const right = decimal(b);
  if (left && right) {
    if (left.sign !== right.sign) return left.sign - right.sign;
    const width = Math.max(left.fraction.length, right.fraction.length);
    const magnitude = left.integer.length - right.integer.length
      || left.integer.localeCompare(right.integer)
      || left.fraction.padEnd(width, '0').localeCompare(right.fraction.padEnd(width, '0'));
    return left.sign * magnitude;
  }
  return cellText(a).localeCompare(cellText(b), undefined, { numeric: true, sensitivity: 'base' });
}

export function resultRows(artifact, search = '', sort = null) {
  const needle = search.toLocaleLowerCase();
  const rows = artifact.rows.filter(row => !needle || row.some(cell => cellText(cell).toLocaleLowerCase().includes(needle)));
  if (!sort || !Number.isInteger(sort.column) || sort.column < 0 || sort.column >= artifact.columns.length) return rows;
  return rows.sort((a, b) => {
    const left = a[sort.column]; const right = b[sort.column];
    if (left === null || right === null) return left === right ? 0 : left === null ? 1 : -1;
    return compareCells(left, right) * (sort.direction === 'desc' ? -1 : 1);
  });
}

function csvCell(value) {
  const text = value === null ? '' : cellText(value);
  const protectedText = typeof value === 'string' && /^[\s\u0000-\u001f\u007f]*[=+\-@]/u.test(text) ? `'${text}` : text;
  return `"${protectedText.replace(/"/g, '""')}"`;
}

export function resultCsv(artifact, rows = artifact.rows) {
  return [artifact.columns, ...rows].map(row => row.map(csvCell).join(',')).join('\r\n');
}

// The doctor advised on one presentation (prescription.for); any other selection ignores it.
function prescriptionApplies(artifact) {
  const f = artifact.prescription?.for;
  return !!f && f.view === artifact.view && (f.chart_type ?? null) === (artifact.chart_type ?? null)
    && f.x === artifact.x && f.y === artifact.y;
}

export const drawnMarks = new Set(['bar', 'line', 'donut', 'stacked']);

// Rows for the prescribed bars: biggest first when the doctor cut to a top N or swapped a donut for bars.
export function prescribedRows(spec) {
  const { top, mark } = spec.prescription;
  const rows = spec.labels.map((label, i) => ({ label, value: spec.series[0].values[i] }));
  if (mark !== 'bar' || !(top || spec.type === 'doughnut')) return top ? rows.slice(0, top) : rows;
  const sorted = [...rows].sort((a, b) => (b.value ?? -Infinity) - (a.value ?? -Infinity));
  return top ? sorted.slice(0, top) : sorted;
}

// The doctor changed nothing on a short vertical bar answer, so ChatChart keeps drawing it.
export function keepsChatChart(spec) {
  const { mark, horizontal, top } = spec.prescription;
  return mark === 'bar' && !horizontal && !top && spec.type === 'bar';
}

export function resultChart(artifact) {
  if (artifact.chart_type !== undefined && !['bar', 'line', 'doughnut'].includes(artifact.chart_type)) return { ok: false, reason: 'type' };
  const type = artifact.chart_type || 'bar';
  const x = artifact.columns.indexOf(artifact.x); const y = artifact.columns.indexOf(artifact.y);
  if (x < 0 || y < 0 || x === y) return { ok: false, reason: 'columns' };
  const values = artifact.rows.map(row => {
    const value = row[y];
    if (value === null) return null;
    if (!['string', 'number'].includes(typeof value) || !/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?$/i.test(String(value).trim())) return NaN;
    const number = Number(value);
    return Number.isFinite(number) && Math.abs(number) <= Number.MAX_SAFE_INTEGER && compareCells(value, number) === 0 ? number : NaN;
  });
  if (values.some(value => value !== null && !Number.isFinite(value))) return { ok: false, reason: 'numeric' };
  const rescued = prescriptionApplies(artifact) && drawnMarks.has(artifact.prescription.mark) && artifact.prescription.mark !== 'donut';
  if (type === 'doughnut' && !rescued && (values.some(value => value < 0) || !values.some(value => value > 0))) return { ok: false, reason: 'doughnut' };
  const checked = validateChartSpec({
    type, title: artifact.title, labels: artifact.rows.map(row => cellText(row[x])),
    x_label: artifact.x, y_label: artifact.y, series: [{ name: artifact.y, values }],
  });
  return checked.ok ? { ...checked, spec: prescriptionApplies(artifact) ? { ...checked.spec, prescription: artifact.prescription } : checked.spec } : { ok: false, reason: artifact.rows.length ? 'limit' : 'empty' };
}

// The doctor's Hebrew notes for a chart answer, one line; null when it changed nothing.
export function prescriptionNote(artifact) {
  const notes = artifact.prescription?.notes;
  return Array.isArray(notes) && notes.length ? notes.join(' · ') : null;
}

export function resultChartOptions(artifact) {
  const leadingZero = value => typeof value === 'string' && /^[+-]?0\d/.test(value.trim());
  const categories = artifact.columns.filter((column, index) => {
    const values = artifact.rows.map(row => row[index]);
    return values.every(value => typeof value === 'string' && value.trim())
      && new Set(values).size === values.length
      && values.some(value => !decimal(value) || leadingZero(value));
  });
  const measures = artifact.columns.filter((column, index) => {
    const x = artifact.columns.find(other => other !== column);
    return x && artifact.rows.some(row => row[index] !== null)
      && !artifact.rows.some(row => leadingZero(row[index]))
      && resultChart({ ...artifact, chart_type: 'bar', x, y: column }).ok;
  });
  const y = measures.find(column => /revenue|amount|total|sales|הכנס|סכום|סה"כ|סה״כ|מחיר|כמות|משקל/i.test(column))
    || measures.find(column => /quantity|qty/i.test(column)) || measures[0];
  const eligible = artifact.rows.length >= 2 && categories.length && y;
  return { categories, measures, x: eligible ? categories[0] : null, y: eligible ? y : null };
}

// The canvas payload's presentation: the picked view, and for a chart its columns and type.
export function canvasArtifact({ view, x, y, chart_type }) {
  return { view, ...(view === 'chart' ? { x, y, ...(chart_type ? { chart_type } : {}) } : {}) };
}

export function resultTree(artifact) {
  const parentColumn = artifact.columns.indexOf(artifact.parent); const childColumn = artifact.columns.indexOf(artifact.child);
  if (parentColumn < 0 || childColumn < 0 || parentColumn === childColumn) return { ok: false, reason: 'columns' };
  if (artifact.rows.some(row => row[childColumn] === null || !scalar(row[parentColumn]) || !scalar(row[childColumn]))) return { ok: false, reason: 'nodes' };
  const edges = artifact.rows.map((row, index) => ({
    parent: row[parentColumn] === null ? null : JSON.stringify(row[parentColumn]), child: JSON.stringify(row[childColumn]), row, index,
  }));
  const grouped = new Map();
  for (const edge of edges) {
    if (!grouped.has(edge.parent)) grouped.set(edge.parent, []);
    grouped.get(edge.parent).push(edge);
  }
  const values = new Map(artifact.rows.flatMap(row => [row[parentColumn], row[childColumn]].filter(value => value !== null).map(value => [JSON.stringify(value), value])));
  const nodes = new Map([...values].map(([key, value]) => [key, { value, edges: grouped.get(key) || [] }]));
  const children = new Set(edges.filter(edge => edge.parent !== null).map(edge => edge.child));
  const explicitRoots = new Set((grouped.get(null) || []).map(edge => edge.child));
  const initialRoots = [...new Set([...explicitRoots, ...nodes.keys()].filter(key => !children.has(key) || explicitRoots.has(key)))];
  const roots = [...initialRoots]; const visited = new Set();
  function visit(key) {
    const queue = [key];
    for (let index = 0; index < queue.length; index++) {
      if (visited.has(queue[index])) continue;
      visited.add(queue[index]);
      for (const edge of nodes.get(queue[index]).edges) queue.push(edge.child);
    }
  }
  initialRoots.forEach(visit);
  for (const key of nodes.keys()) {
    if (!visited.has(key)) { roots.push(key); visit(key); }
  }
  return { ok: true, nodes, roots, edges, rootEdges: grouped.get(null) || [] };
}
